import { vehicleSelected } from 'Core/actions/fitmentSearch';
import {
  assignLocation,
  goBackInHistory,
  pushToHistory,
  replaceInHistory,
  replaceLocation,
} from 'Core/actions/redirect.ts';
import { setServerRedirect } from 'Core/actions/response';
import { epicFromHandlers } from 'Core/epics/common.js';
import history from 'Core/history.js';
import { serverRedirectSelector } from 'Core/selectors/search';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import requestConfig from 'Models/uiConfig/requestConfig';
import { getUriFromRequest } from 'Modules/converter';

export default epicFromHandlers({
  [assignLocation.type]: ({ state, action: { payload } }) => {
    const serverRedirect = serverRedirectSelector(state);
    window.location.assign(serverRedirect || payload);
  },
  [replaceLocation.type]: ({ action: { payload } }) => window.location.replace(payload),
  [goBackInHistory.type]: () => history.back(),
  [pushToHistory.type]: ({
    action: {
      payload: { location, state },
    },
  }) =>
    // sometimes history manipulations force style recalculation so it's better to do it a bit later
    void setTimeout(() => {
      history.push(location, state);
      window.ga?.('send', 'pageview', window.location.pathname + window.location.search);
    }, 0),
  [replaceInHistory.type]: ({
    action: {
      payload: { location, state },
    },
  }) =>
    void setTimeout(() => {
      history.replace(location, state);
    }, 0),
});

export function tryResponseRedirect(arg, response) {
  const {
    dispatch,
    action: { meta: { doNotRedirectOnVehicleSelect, onInit } = {}, type: actionType },
  } = arg;

  const isVehicleSelectedAction = actionType === vehicleSelected.type;

  if (isVehicleSelectedAction && (onInit || doNotRedirectOnVehicleSelect)) {
    return false;
  }

  const { Actions: [{ Type, RedirectUrl: serverRedirect } = {}] = [] } = response;
  if (Type === 'Redirect' && serverRedirect) {
    if (window.location.pathname !== serverRedirect) {
      const { platformRoute } = requestConfig;
      const redirectUrl =
        platformRoute && !serverRedirect.startsWith(platformRoute)
          ? `${platformRoute}${serverRedirect}`
          : serverRedirect;

      dispatch(setServerRedirect(redirectUrl));
      dispatch(assignLocation(redirectUrl));
      return true;
    }

    return false;
  }

  const { vehicleLandingRedirectToSearch } = response.Extra || {};
  return isVehicleSelectedAction ? tryVehicleRedirect(arg, vehicleLandingRedirectToSearch) : false;
}

export function tryVehicleRedirect(arg, vehicleLandingRedirectToSearch) {
  const {
    dispatch,
    action: {
      payload: selectedVehicle,
      meta: {
        isSpecifyDialogOpened,
        isSelectedFromDialog,
        isSelectedFromGarage,
        isSelectedFromHeader,
        isSelectedFromSearch,
        isVehicleSelectionForced,
        isVehicleAutoSelected,
        redirectUrl,
      } = {},
    },
  } = arg;

  const {
    categorySelectionPageUrl,
    isAutoRedirectDisabled,
    isCategorySelectionPageRedirectEnabled,
    isOnCategorySelectionPage,
  } = fitmentSearchConfig;

  const isSelectedFromIsolatedHeader = isSelectedFromHeader && !isOnCategorySelectionPage;
  const isSelectedFromIsolatedDialog =
    isSelectedFromDialog && !isSpecifyDialogOpened && !isVehicleSelectionForced;
  const isSelectedFromSearchWithRedirect = isSelectedFromSearch && redirectUrl;

  const isWidgetRedirectAllowed =
    isSelectedFromIsolatedHeader ||
    isSelectedFromIsolatedDialog ||
    isSelectedFromGarage ||
    isSelectedFromSearchWithRedirect;

  const isVehicleSelectionRedirectAllowed =
    !isVehicleAutoSelected || (isVehicleAutoSelected && !isAutoRedirectDisabled);

  if ((isWidgetRedirectAllowed || vehicleLandingRedirectToSearch) && isVehicleSelectionRedirectAllowed) {
    const selectedVehicleSelection = selectedVehicle.selection;

    if (redirectUrl) {
      return tryVehicleRedirectByUrl(dispatch, redirectUrl, selectedVehicleSelection);
    }

    if (!isCategorySelectionPageRedirectEnabled || !categorySelectionPageUrl) {
      const searchPageRedirectUri = getUriFromRequest(
        { selection: selectedVehicleSelection },
        { goToSearchPage: true },
      );

      if (window.location.pathname !== searchPageRedirectUri.pathname) {
        dispatch(assignLocation(searchPageRedirectUri.href));
        return true;
      }

      return false;
    }

    if (isCategorySelectionPageRedirectEnabled && !isOnCategorySelectionPage) {
      dispatch(
        assignLocation(
          getUriFromRequest({ selection: selectedVehicleSelection }, { pathname: categorySelectionPageUrl })
            .href,
        ),
      );

      return true;
    }
  }

  return false;
}

function tryVehicleRedirectByUrl(dispatch, redirectUrl, selectedVehicleSelection) {
  if (redirectUrl && window.location.pathname !== redirectUrl) {
    dispatch(
      assignLocation(
        getUriFromRequest({ selection: selectedVehicleSelection }, { pathname: redirectUrl }).href,
      ),
    );

    return true;
  }

  return false;
}
